/**
 * REALTY-LIFECYCLE-E1 — canonical frontend Property lifecycle contract.
 *
 * Pure helpers only: no API, Redux, localStorage, or React.
 * Owner transitions mirror apps.realty.services B1 matrix.
 */

export const PROPERTY_STATUSES = [
  "draft",
  "active",
  "reserved",
  "sold",
  "archived",
];

export const PROPERTY_STATUS_META = {
  draft: {
    label: "Чернетка",
    tone: "draft",
    description: "Оголошення ще не опубліковане.",
    visibilityHint: "Не видно у пошуку",
  },
  active: {
    label: "Активне",
    tone: "active",
    description: "Оголошення доступне в публічному каталозі.",
    visibilityHint: "Видно у пошуку",
  },
  reserved: {
    label: "Зарезервовано",
    tone: "reserved",
    description:
      "Об'єкт тимчасово недоступний для нових покупців або орендарів.",
    visibilityHint: "Не у публічному каталозі",
  },
  sold: {
    label: "Продано",
    tone: "sold",
    description: "Угоду щодо об'єкта завершено.",
    visibilityHint: "Не у активному пошуку",
  },
  archived: {
    label: "В архіві",
    tone: "archived",
    description: "Оголошення приховане з публічного каталогу.",
    visibilityHint: "Не у публічному каталозі",
  },
};

const MODERATION_LOCK_NOTICE =
  "Оголошення приховане модерацією. Самостійне відновлення недоступне.";

const SOLD_ACTION_LABELS = {
  sale: "Позначити як продане",
  rent: "Позначити угоду завершеною",
};

const SOLD_CONFIRM_TITLE = {
  sale: "Позначити як продане",
  rent: "Завершити угоду",
};

const SOLD_SUCCESS = {
  sale: "Оголошення позначено як продане.",
  rent: "Оголошення позначено як завершене.",
};

/** B1 owner-allowed edges (forbidden transitions omitted). */
const OWNER_EDGE_ACTIONS = {
  draft: ["publish", "archive"],
  active: ["unpublish", "reserve", "sold", "archive"],
  reserved: ["return_active", "sold", "archive"],
  sold: ["reactivate", "archive"],
  archived: ["reactivate"],
};

const ACTION_DEFS = {
  publish: {
    id: "publish",
    label: "Опублікувати",
    targetStatus: "active",
    modalId: "content-setting-publish",
    icon: "fa-solid fa-eye",
    requiresActivation: true,
  },
  unpublish: {
    id: "unpublish",
    label: "Зняти з публікації",
    targetStatus: "draft",
    modalId: "content-setting-unpublish",
    icon: "fa-solid fa-eye-slash",
  },
  reserve: {
    id: "reserve",
    label: "Зарезервувати",
    targetStatus: "reserved",
    modalId: "content-setting-reserve",
    // Distinct from save-bookmark (fa-bookmark) — ACTION-MENUS-UX-B1-19.
    icon: "fa-solid fa-calendar-check",
  },
  return_active: {
    id: "return_active",
    label: "Повернути в активні",
    targetStatus: "active",
    modalId: "content-setting-reactivate",
    icon: "fa-solid fa-rotate-left",
    requiresActivation: true,
    successClass: "action-btn--success",
  },
  reactivate: {
    id: "reactivate",
    label: "Активувати",
    targetStatus: "active",
    modalId: "content-setting-reactivate",
    icon: "fa-solid fa-rotate-left",
    requiresActivation: true,
    blockedIfModerationLocked: true,
    successClass: "action-btn--success",
  },
  sold: {
    id: "sold",
    label: "Позначити як продане",
    targetStatus: "sold",
    modalId: "content-setting-sold-out",
    icon: "fa-solid fa-handshake",
    successClass: "action-btn--success",
  },
  archive: {
    id: "archive",
    label: "В архів",
    targetStatus: "archived",
    modalId: "content-setting-archive",
    icon: "fa-solid fa-box-archive",
  },
  delete: {
    id: "delete",
    label: "Видалити оголошення",
    modalId: "content-setting-delete",
    icon: "fa-solid fa-trash",
    destructive: true,
    dangerClass: "action-btn--danger",
  },
  edit: {
    id: "edit",
    label: "Редагувати",
    icon: "fa-solid fa-pen-to-square",
    navigation: true,
  },
};

export function getPropertyStatusLabel(status) {
  return PROPERTY_STATUS_META[status]?.label || "Невідомий статус";
}

export function getPropertyStatusTone(status) {
  return PROPERTY_STATUS_META[status]?.tone || "draft";
}

export function getPropertyStatusDescription(status) {
  return PROPERTY_STATUS_META[status]?.description || "";
}

export function getPropertyVisibilityHint(status) {
  return PROPERTY_STATUS_META[status]?.visibilityHint || "";
}

export function isModerationLocked(property) {
  if (!property) return false;
  if (typeof property.is_moderation_locked === "boolean") {
    return property.is_moderation_locked;
  }
  return Boolean(property.moderation_hidden_at);
}

export function getLifecycleNotice(property) {
  if (!property) return null;
  const status = property.status || "draft";
  if (status === "archived" && isModerationLocked(property)) {
    return MODERATION_LOCK_NOTICE;
  }
  if (status === "archived") {
    return "Оголошення в архіві. Ви можете активувати його знову або видалити.";
  }
  if (status === "sold") {
    const dealType = property.deal_type || "sale";
    if (dealType === "rent") {
      return "Угоду завершено. Ви можете активувати оголошення знову або видалити.";
    }
    return "Оголошення продано. Ви можете активувати його знову або видалити.";
  }
  return getPropertyStatusDescription(status);
}

export function getSoldActionLabel(dealType) {
  return SOLD_ACTION_LABELS[dealType] || SOLD_ACTION_LABELS.sale;
}

export function getStatusSuccessMessage(targetStatus, options = {}) {
  const dealType = options.dealType || "sale";
  const fromStatus = options.fromStatus;
  if (targetStatus === "sold") {
    return SOLD_SUCCESS[dealType] || SOLD_SUCCESS.sale;
  }
  if (targetStatus === "active") {
    if (fromStatus === "draft") {
      return "Оголошення опубліковано.";
    }
    return "Оголошення активовано.";
  }
  const map = {
    draft: "Оголошення знято з публікації.",
    reserved: "Оголошення позначено як зарезервоване.",
    archived: "Оголошення перенесено в архів.",
  };
  return map[targetStatus] || "Статус оновлено.";
}

export function getLifecycleConfirmation(actionId, property) {
  const dealType = (property && property.deal_type) || "sale";
  const confirmations = {
    publish: {
      title: "Опублікувати",
      body:
        "Оголошення стане активним і з’явиться у пошуку. Переконайтеся, що локація та характеристики заповнені.",
      confirmLabel: "Опублікувати",
    },
    unpublish: {
      title: "Зняти з публікації",
      body:
        "Оголошення зникне з пошуку та повернеться у статус «Чернетка». Дані залишаться збереженими.",
      confirmLabel: "Зняти",
    },
    reserve: {
      title: "Зарезервувати об'єкт?",
      body:
        "Оголошення буде тимчасово недоступне для нових звернень відповідно до поточних правил публічного каталогу.",
      confirmLabel: "Зарезервувати",
    },
    return_active: {
      title: "Повернути в активні",
      body:
        "Оголошення повернеться у статус «Активне» і знову з’явиться у пошуку.",
      confirmLabel: "Активувати",
    },
    reactivate: {
      title: "Активувати оголошення",
      body:
        "Оголошення повернеться у статус «Активне» і знову з’явиться у пошуку.",
      confirmLabel: "Активувати",
    },
    sold: {
      title: SOLD_CONFIRM_TITLE[dealType] || SOLD_CONFIRM_TITLE.sale,
      body:
        dealType === "rent"
          ? "Статус оголошення буде змінено. Об'єкт більше не відображатиметься як активна пропозиція."
          : "Статус оголошення буде змінено на «Продано».",
      confirmLabel: dealType === "rent" ? "Так, завершено" : "Так, продано",
    },
    archive: {
      title: "Перемістити оголошення в архів?",
      body: "Оголошення буде приховане з публічного каталогу.",
      confirmLabel: "В архів",
    },
    delete: {
      title: "Видалити оголошення",
      body:
        "Дію не можна відмінити. Оголошення та всі його файли будуть видалені без можливості відновлення.",
      confirmLabel: "Видалити",
    },
  };
  return confirmations[actionId] || null;
}

/**
 * @param {object} property — Property payload with status (+ optional is_moderation_locked)
 * @param {{ isOwner?: boolean }} userContext
 */
export function getOwnerLifecycleActions(property, userContext = {}) {
  if (!property || !userContext.isOwner) {
    return [];
  }

  const status = property.status || "draft";
  const dealType = property.deal_type || "sale";
  const edgeIds = OWNER_EDGE_ACTIONS[status] || [];
  const actions = [];

  for (const actionId of edgeIds) {
    const def = ACTION_DEFS[actionId];
    if (!def) continue;
    if (def.blockedIfModerationLocked && isModerationLocked(property)) {
      continue;
    }
    const action = { ...def };
    if (actionId === "sold") {
      action.label = getSoldActionLabel(dealType);
    }
    actions.push(action);
  }

  actions.push({ ...ACTION_DEFS.delete });
  return actions;
}

export function statusRequiresActivation(targetStatus) {
  return targetStatus === "active";
}

export function getManageZoneLayout(property) {
  const status = property?.status || "draft";
  const locked = isModerationLocked(property);
  const actions = getOwnerLifecycleActions(property, { isOwner: true });
  const statusActions = actions.filter((a) => a.id !== "delete");
  const hasDelete = actions.some((a) => a.id === "delete");

  return {
    notice: getLifecycleNotice(property),
    statusActions,
    hasDelete,
    showModerationLock: status === "archived" && locked,
    isTerminalCollapsed: status === "sold" || (status === "archived" && !locked),
  };
}
